"use client"

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react"
import type { ViewStageConfigurationDto, StageFilterDto, FilterDto } from "@/types/saved-view"

/**
 * Entities that participate in the unified Requests view group. Mirrors the
 * BE's `ViewGroupCode = "Requests"` group from `WorkflowHubV2Service`. Keep
 * this in sync with the BE list — adding a new sibling here without seeding
 * its configurations or wiring its publisher is a no-op (the modal just
 * won't surface dirty state for it), so adding new entries is safe.
 */
export const REQUESTS_GROUP_ENTITY_TYPES = [
  "ClientRequest",
  "VmsRequest",
  "VmsEmail",
  "AtsPlacementRequest",
] as const

export type RequestsGroupEntityType = (typeof REQUESTS_GROUP_ENTITY_TYPES)[number]

export function isRequestsGroupEntity(entityType: string | undefined | null): entityType is RequestsGroupEntityType {
  return !!entityType && (REQUESTS_GROUP_ENTITY_TYPES as readonly string[]).includes(entityType)
}

/**
 * Snapshot of an individual sibling's editable view state. Exposed to the
 * save modal so it can build a `saveViewGroup` payload without having to
 * re-cross-talk into each sibling's WorkflowProvider at save time.
 *
 * Kept minimal — only the bits the BE actually persists on a ViewSettings
 * sibling (column choices, per-stage filters, global filters). Filters
 * propagate identically across siblings, so the modal will typically use
 * the active tab's globalFilters as the canonical source rather than each
 * sibling's snapshot copy.
 */
export interface RequestsGroupTabSnapshot {
  /** Currently loaded view (null = no named view, default-view fallback). */
  currentViewSettingsId: string | null
  /** Group association of the loaded view ("Requests" or null for legacy entity-scoped views). */
  currentViewGroupCode: string | null
  /** Title of the loaded view — used to pair siblings of the same group. */
  currentViewTitle: string | null
  /** Loaded view is the tenant default. Drives the ModifyDefaultView permission gate. */
  currentViewIsDefault: boolean
  /** Loaded view is public. Drives the ModifyPublicViews permission gate. */
  currentViewIsPublic: boolean
  /**
   * The publisher considers itself the owner of the loaded view — i.e. it's
   * in that tab's userViews list. Owner can always save without an extra
   * authorized-action check; non-owners fall back to the default / public /
   * shared rules. Set by the WorkflowProvider's publish effect from its
   * own viewsResponse — each tab knows whether the user owns its sibling
   * of the group view, the parent save-modal consumer doesn't have to
   * cross-reference per-entity lists.
   */
  currentViewIsOwner: boolean
  /** True when the user has edited filters / stage configs since loading the current view. */
  hasUnsavedChanges: boolean
  /** Latest editable state, ready to be saved. */
  stageConfigurations: ViewStageConfigurationDto[]
  stageFilters: StageFilterDto[]
  globalFilters: FilterDto[]
}

/**
 * Refresh signal payload. When one tab does something that affects sibling
 * tabs' server-side state (save view, delete view, toggle visibility, etc.),
 * it bumps this counter via {@link RequestsGroupStateValue.bumpRefreshSignal}.
 * Sibling providers subscribe to the counter and call refreshConfiguration
 * when it changes — that's how we keep already-mounted providers fresh
 * without forcing the user to navigate away and back.
 *
 * `origin` records the entityType that triggered the bump so subscribers
 * can skip re-fetching themselves (the originating tab has already
 * processed the change).
 */
export interface RequestsGroupRefreshSignal {
  /** Monotonically-increasing counter. Equality of this against the
   * previous value is the trigger for subscribers. */
  nonce: number
  /** EntityType that bumped the signal. Subscribers may use this to
   * skip-self when their own action triggered the bump. Null for an
   * un-scoped fan-out (e.g. delete-driven full refresh). */
  origin: RequestsGroupEntityType | null
}

interface RequestsGroupStateValue {
  /** Map keyed by entityType. Tabs that haven't mounted (or aren't in the group) are absent. */
  tabs: Partial<Record<RequestsGroupEntityType, RequestsGroupTabSnapshot>>
  /**
   * Publishes the calling tab's snapshot to the shared store. Each
   * WorkflowProvider for a Requests-group entity should call this whenever
   * its dirty flag or active view changes — debounced or React-scheduled is
   * fine, the consumers only read at save time.
   */
  publishTab: (entityType: RequestsGroupEntityType, snapshot: RequestsGroupTabSnapshot) => void
  /**
   * Clears a tab's snapshot — used when a WorkflowProvider unmounts (e.g.
   * tabs that were never visited) so stale entries don't linger in the
   * modal's dirty list.
   */
  clearTab: (entityType: RequestsGroupEntityType) => void
  /**
   * Current refresh signal. Sibling WorkflowProviders subscribe to this via
   * a useEffect dependency; when {@link nonce} changes (and the origin
   * isn't the subscriber's own entity), they call refreshConfiguration to
   * reload server-side state.
   */
  refreshSignal: RequestsGroupRefreshSignal
  /**
   * Trigger a refresh fan-out. Called after a save / delete / visibility
   * change that affects sibling tabs' BE state. `origin` is the entityType
   * that initiated the change — passed through to subscribers so they can
   * skip-self (the originating tab already has the fresh data via its own
   * response). Pass null to force a full fan-out where every subscriber
   * including the origin re-fetches.
   */
  bumpRefreshSignal: (origin: RequestsGroupEntityType | null) => void
}

const RequestsGroupStateContext = createContext<RequestsGroupStateValue | null>(null)

/**
 * Provider for the cross-tab Requests group state. Mount once at the
 * workflow-hub level (above all four Requests tab providers). Without a
 * provider in the tree, the {@link useRequestsGroupState} hook returns
 * `null` so legacy entity tabs (WorkOrder) and any caller that doesn't
 * care about the group simply opt out — no error is thrown.
 */
export function RequestsGroupStateProvider({ children }: { children: ReactNode }) {
  // useState for re-render on tab updates; the ref backup gives publishTab a
  // stable identity across renders so child useEffects don't refire just
  // because the callback changed.
  const [tabs, setTabs] = useState<RequestsGroupStateValue["tabs"]>({})
  const tabsRef = useRef(tabs)
  tabsRef.current = tabs

  const publishTab = useCallback<RequestsGroupStateValue["publishTab"]>((entityType, snapshot) => {
    setTabs((prev) => {
      const existing = prev[entityType]
      // Shallow stability check — avoid causing a re-render when nothing
      // meaningful changed. This matters because the WorkflowProvider's
      // publish effect re-runs on every state change; if the snapshot is
      // semantically the same we drop the update.
      if (
        existing &&
        existing.hasUnsavedChanges === snapshot.hasUnsavedChanges &&
        existing.currentViewSettingsId === snapshot.currentViewSettingsId &&
        existing.currentViewGroupCode === snapshot.currentViewGroupCode &&
        existing.currentViewTitle === snapshot.currentViewTitle &&
        existing.currentViewIsDefault === snapshot.currentViewIsDefault &&
        existing.currentViewIsPublic === snapshot.currentViewIsPublic &&
        existing.currentViewIsOwner === snapshot.currentViewIsOwner &&
        existing.stageConfigurations === snapshot.stageConfigurations &&
        existing.stageFilters === snapshot.stageFilters &&
        existing.globalFilters === snapshot.globalFilters
      ) {
        return prev
      }
      return { ...prev, [entityType]: snapshot }
    })
  }, [])

  const clearTab = useCallback<RequestsGroupStateValue["clearTab"]>((entityType) => {
    setTabs((prev) => {
      if (!(entityType in prev)) return prev
      const next = { ...prev }
      delete next[entityType]
      return next
    })
  }, [])

  // Refresh signal — starts at nonce 0, no origin. Each bump replaces the
  // object so identity changes (triggering useEffect subscriptions even when
  // origin happens to repeat).
  const [refreshSignal, setRefreshSignal] = useState<RequestsGroupRefreshSignal>({ nonce: 0, origin: null })

  const bumpRefreshSignal = useCallback<RequestsGroupStateValue["bumpRefreshSignal"]>((origin) => {
    setRefreshSignal((prev) => ({ nonce: prev.nonce + 1, origin }))
  }, [])

  const value = useMemo<RequestsGroupStateValue>(
    () => ({ tabs, publishTab, clearTab, refreshSignal, bumpRefreshSignal }),
    [tabs, publishTab, clearTab, refreshSignal, bumpRefreshSignal],
  )

  return (
    <RequestsGroupStateContext.Provider value={value}>
      {children}
    </RequestsGroupStateContext.Provider>
  )
}

/**
 * Subscribes to the cross-tab Requests group state. Returns `null` when
 * called outside a {@link RequestsGroupStateProvider} so existing callers
 * (WorkOrder tab, anywhere else) don't have to be wrapped — they just opt
 * out by checking for null.
 */
export function useRequestsGroupState(): RequestsGroupStateValue | null {
  return useContext(RequestsGroupStateContext)
}

/**
 * Convenience: returns the list of OTHER tabs (excluding the caller's
 * entityType) that currently have unsaved changes against a Requests-group
 * view. Driven by the save modal when deciding which siblings to surface.
 *
 * @param currentEntityType the tab the save modal is opened on
 */
export function selectDirtyOtherTabs(
  state: RequestsGroupStateValue | null,
  currentEntityType: RequestsGroupEntityType,
): Array<{ entityType: RequestsGroupEntityType; snapshot: RequestsGroupTabSnapshot }> {
  if (!state) return []
  const out: Array<{ entityType: RequestsGroupEntityType; snapshot: RequestsGroupTabSnapshot }> = []
  for (const entityType of REQUESTS_GROUP_ENTITY_TYPES) {
    if (entityType === currentEntityType) continue
    const snapshot = state.tabs[entityType]
    if (snapshot?.hasUnsavedChanges) {
      out.push({ entityType, snapshot })
    }
  }
  return out
}
